import React, { useState, useEffect, createContext, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, List, Settings, LogOut, Menu, X, 
  BarChart3, FileText, Moon, Sun, ChevronRight, ChevronDown,
  ChevronLeft, CheckSquare, Smile, Lightbulb, Lock,
  MessageSquareText, MessageCircle, Palette, Vote, Shield, Users,
  Megaphone, ClipboardList, Trophy, BookOpen, Bell, Search, Wallet, Send,
  Inbox, Activity, ArrowLeft, Loader2, Bot, Crosshair, Star, Edit2, Package, CheckCircle2, Calendar, Target, PieChart, Stethoscope, FolderKanban
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useTheme } from '@/contexts/ThemeContext';
import { useLanguage } from '@/context/LanguageContext';
import { DailyPopups } from '@/components/DailyPopups';
import { OnlineUsersIndicator } from '@/components/shared/OnlineUsersIndicator';
import { useWebSocket } from '@/contexts/WebSocketContext';
import { clearPersistedViewState } from '@/hooks/usePersistedState';

// Sidebar context for collapse state
const SidebarContext = createContext(undefined);

export function useSidebar() {
  const context = useContext(SidebarContext);
  if (!context) {
    return { isCollapsed: false, setIsCollapsed: () => {} };
  }
  return context;
}


// Notification Panel Component with Tabs (All, Activity, Mention)
function NotificationPanel({ notifications, setNotifications, onClose, onNavigate }) {
  const [activeTab, setActiveTab] = useState('all');
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');
  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
  const h = { Authorization: `Bearer ${token}` };

  const markAllRead = async () => {
    try {
      await fetch(`${API_URL}/api/notifications/read-all`, { method: 'PUT', headers: h });
      setNotifications(notifications.map(n => ({ ...n, read: true })));
    } catch {}
  };

  const markRead = async (id) => {
    try {
      await fetch(`${API_URL}/api/notifications/${id}/read`, { method: 'PUT', headers: h });
      setNotifications(prev => prev.map(x => x.id === id ? { ...x, read: true } : x));
    } catch {}
  };

  // Filter: Channel = isu/progress channel · Activity = hal user/team · Mention = assigned/mentioned
  const mentionTypes = ['assigned', 'mention', 'mentioned'];
  const channelTypes = ['renewal', 'performance_alert', 'diagnosis', 'segment_alert'];
  const channelNotifs = notifications.filter(n => channelTypes.includes(n.type));
  const activityNotifs = notifications.filter(n => !mentionTypes.includes(n.type) && !channelTypes.includes(n.type));
  const mentionNotifs = notifications.filter(n => mentionTypes.includes(n.type));
  const unreadActivity = activityNotifs.filter(n => !n.read).length;
  const unreadMention = mentionNotifs.filter(n => !n.read).length;
  const unreadChannel = channelNotifs.filter(n => !n.read).length;

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const diffMs = Date.now() - d.getTime();
    if (diffMs < 60000) return 'just now';
    if (diffMs < 3600000) return `${Math.floor(diffMs / 60000)}m`;
    if (diffMs < 86400000) return `${Math.floor(diffMs / 3600000)}h`;
    return d.toLocaleDateString('en-MY', { day: 'numeric', month: 'short' });
  };

  const getDisplayList = () => {
    if (activeTab === 'channel') return channelNotifs;
    if (activeTab === 'activity') return activityNotifs;
    if (activeTab === 'mention') return mentionNotifs;
    return notifications;
  };

  const tabs = [
    { id: 'all', label: 'All', count: notifications.filter(n => !n.read).length },
    { id: 'channel', label: 'Channel', count: unreadChannel },
    { id: 'activity', label: 'Activity', count: unreadActivity },
    { id: 'mention', label: 'Mention', count: unreadMention },
  ];

  const displayList = getDisplayList();

  return (
    <div className="absolute top-11 right-0 w-96 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl shadow-2xl overflow-hidden z-[9999]" data-testid="notif-panel" style={{ maxHeight: 'min(480px, 80vh)' }}>
      <div className="p-3 border-b border-[hsl(var(--border))] flex items-center justify-between">
        <span className="font-semibold text-sm">Notifications</span>
        {notifications.filter(n => !n.read).length > 0 && <button onClick={markAllRead} className="text-[10px] text-[hsl(var(--primary))] hover:underline">Mark all read</button>}
      </div>
      <div className="flex border-b border-[hsl(var(--border))]">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`notif-tab-${tab.id}`}
            className={`flex-1 py-2 text-xs font-medium transition-colors relative ${activeTab === tab.id ? 'text-[hsl(var(--primary))]' : 'text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text-secondary))]'}`}>
            {tab.label}
            {tab.count > 0 && <span className="ml-1 w-4 h-4 bg-red-500 text-white text-[9px] rounded-full inline-flex items-center justify-center">{tab.count}</span>}
            {activeTab === tab.id && <span className="absolute bottom-0 left-1/4 right-1/4 h-0.5 bg-[hsl(var(--primary))] rounded-full" />}
          </button>
        ))}
      </div>
      <div className="overflow-y-auto" style={{ maxHeight: '360px' }}>
        {displayList.length === 0 ? (
          <div className="p-8 text-center text-sm text-[hsl(var(--text-muted))]">
            <Bell size={24} className="mx-auto mb-2 opacity-30" />
            {activeTab === 'mention' ? 'No mentions yet' : activeTab === 'channel' ? 'No channel alerts' : 'No notifications'}
          </div>
        ) : displayList.map(n => (
          <div key={n.id} onClick={() => { 
            if (!n.read) markRead(n.id);
            // Navigate based on notification type
            if (onNavigate) {
              const msg = (n.message || '').toLowerCase();
              // Channel notifications → jump straight to the channel page
              if (channelTypes.includes(n.type)) {
                if (n.related_id) {
                  if (n.type === 'renewal') onNavigate(`/ib/${n.related_id}/details?tab=clients`);
                  else if (n.type === 'segment_alert') onNavigate(`/ib/${n.related_id}/details?tab=database`);
                  else onNavigate(`/ib/${n.related_id}/details?tab=dashboard`);
                } else {
                  onNavigate('/ib-management/war-room');
                }
                onClose();
                return;
              }
              // Route based on message content — most specific first
              if (msg.includes('standup')) onNavigate('/team/standup');
              else if (msg.includes('creative request') || msg.includes('request assigned') || n.type === 'assigned' || n.type === 'status_update') onNavigate('/team/creative-request');
              else if (msg.includes('poll')) onNavigate('/team/voting');
              else if (msg.includes('announcement') || msg.includes('posted:')) onNavigate('/team/announcements');
              else if (msg.includes('idea')) onNavigate('/team/ideas-feedback');
              else if (msg.includes('fund') || msg.includes('disburse')) onNavigate('/finance/fund-request');
              else if (msg.includes('feedback') || msg.includes('crm feedback')) onNavigate('/team/ideas-feedback');
              else if (n.type === 'comment' && msg.includes('request')) onNavigate('/team/creative-request');
              else if (n.type === 'mention') onNavigate('/team/creative-request');
              onClose();
            }
          }}
            className={`p-3 border-b border-[hsl(var(--border))] hover:bg-[hsl(var(--surface))] transition-colors cursor-pointer ${!n.read ? 'bg-[hsl(var(--primary))]/5' : ''}`}>
            <div className="flex items-start gap-2">
              <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${!n.read ? 'bg-[hsl(var(--primary))]' : 'bg-transparent'}`} />
              <div className="flex-1">
                <p className="text-xs leading-relaxed">{n.message}</p>
                <p className="text-[10px] text-[hsl(var(--text-muted))] mt-1">{formatTime(n.created_at)}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Inbox Panel Component (Separate from Notifications)
function InboxPanel({ onClose, directUser }) {
  const [conversations, setConversations] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [showNewChat, setShowNewChat] = useState(false);
  const [loadingChat, setLoadingChat] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');
  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
  const h = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchConversations(); }, []);

  // Auto-open direct user chat if provided
  useEffect(() => {
    if (directUser && directUser.id) {
      openChat(directUser.id, directUser.name, directUser.avatar_url);
    }
  }, [directUser]);

  const fetchConversations = async () => {
    try {
      const res = await fetch(`${API_URL}/api/inbox/conversations`, { headers: h });
      if (res.ok) { const data = await res.json(); setConversations(data.conversations || []); }
    } catch {}
  };

  const openChat = async (partnerId, partnerName, partnerAvatar) => {
    setSelectedChat({ id: partnerId, name: partnerName, avatar: partnerAvatar });
    setLoadingChat(true);
    try {
      const res = await fetch(`${API_URL}/api/inbox/messages/${partnerId}`, { headers: h });
      if (res.ok) { const data = await res.json(); setChatMessages(data.messages || []); }
    } catch {}
    finally { setLoadingChat(false); }
  };

  const sendMsg = async () => {
    if (!newMessage.trim() || !selectedChat) return;
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/inbox/send`, {
        method: 'POST', headers: { ...h, 'Content-Type': 'application/json' },
        body: JSON.stringify({ to_id: selectedChat.id, message: newMessage.trim() })
      });
      if (res.ok) { const data = await res.json(); setChatMessages(prev => [...prev, data.message]); setNewMessage(''); }
    } catch {}
    finally { setSending(false); }
  };

  const startNewChat = async () => {
    if (teamMembers.length === 0) {
      try {
        const res = await fetch(`${API_URL}/api/team-members`, { headers: h });
        if (res.ok) { const data = await res.json(); setTeamMembers((data.members || []).filter(m => m.id !== currentUser.id)); }
      } catch {}
    }
    setShowNewChat(true);
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const diffMs = Date.now() - new Date(dateStr).getTime();
    if (diffMs < 60000) return 'now';
    if (diffMs < 3600000) return `${Math.floor(diffMs / 60000)}m`;
    if (diffMs < 86400000) return `${Math.floor(diffMs / 3600000)}h`;
    return new Date(dateStr).toLocaleDateString('en-MY', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="absolute top-11 right-0 w-96 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl shadow-2xl overflow-hidden z-[9999]" style={{ maxHeight: 'min(520px, 80vh)' }}>
      <div className="p-3 border-b border-[hsl(var(--border))]">
        {selectedChat ? (
          <div className="flex items-center gap-2">
            <button onClick={() => setSelectedChat(null)} className="p-1 hover:bg-[hsl(var(--surface))] rounded-md"><ArrowLeft size={16} /></button>
            {selectedChat.avatar ? <img src={selectedChat.avatar} className="w-6 h-6 rounded-full" alt="" /> : <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-[10px] font-bold">{selectedChat.name?.charAt(0)}</div>}
            <span className="font-semibold text-sm">{selectedChat.name}</span>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <span className="font-semibold text-sm flex items-center gap-2"><MessageCircle size={16} className="text-blue-400" /> Messages</span>
          </div>
        )}
      </div>
      <div className="overflow-y-auto" style={{ maxHeight: selectedChat ? '400px' : '420px' }}>
        {selectedChat ? (
          <div className="flex flex-col">
            <div className="flex-1 p-3 space-y-2 overflow-y-auto" style={{ maxHeight: '340px' }}>
              {loadingChat ? <div className="text-center py-8"><Loader2 size={20} className="animate-spin mx-auto text-[hsl(var(--text-muted))]" /></div> :
              chatMessages.length === 0 ? <p className="text-center text-xs text-[hsl(var(--text-muted))] py-8">Start a conversation</p> :
              chatMessages.map((m, idx) => {
                // Show date separator when date changes
                const msgDate = new Date(m.created_at).toLocaleDateString('en-MY', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
                const prevDate = idx > 0 ? new Date(chatMessages[idx-1].created_at).toLocaleDateString('en-MY', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : null;
                const showDate = idx === 0 || msgDate !== prevDate;
                return (
                <React.Fragment key={m.id}>
                  {showDate && <div className="text-center my-2"><span className="text-[9px] bg-[hsl(var(--surface))] text-[hsl(var(--text-muted))] px-3 py-0.5 rounded-full">{msgDate}</span></div>}
                  <div className={`flex ${m.from_id === currentUser.id ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[75%] px-3 py-2 rounded-xl text-xs ${m.from_id === currentUser.id ? 'bg-blue-500/20 text-[hsl(var(--text-primary))]' : 'bg-[hsl(var(--surface))] text-[hsl(var(--text-secondary))]'}`}>
                    <p>{m.message}</p>
                    <p className="text-[9px] text-[hsl(var(--text-muted))] mt-1 text-right">{formatTime(m.created_at)}</p>
                  </div>
                </div>
                </React.Fragment>
              );
              })}
            </div>
            <div className="p-2 border-t border-[hsl(var(--border))] flex gap-2">
              <input value={newMessage} onChange={e => setNewMessage(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendMsg()} placeholder="Type a message..." className="flex-1 bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500" />
              <button onClick={sendMsg} disabled={sending || !newMessage.trim()} className="p-2 bg-blue-500 text-white rounded-lg disabled:opacity-40"><Send size={14} /></button>
            </div>
          </div>
        ) : (
          <div>
            <div className="p-2 border-b border-[hsl(var(--border))]">
              <button onClick={startNewChat} className="w-full text-xs text-blue-400 hover:bg-[hsl(var(--surface))] rounded-lg py-2 font-medium transition-colors">+ New Message</button>
            </div>
            {showNewChat && (
              <div className="border-b border-[hsl(var(--border))] p-2 space-y-1 max-h-40 overflow-y-auto bg-[hsl(var(--surface))]/50">
                {teamMembers.map(m => (
                  <button key={m.id} onClick={() => { openChat(m.id, m.name, m.avatar_url); setShowNewChat(false); }}
                    className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-[hsl(var(--surface))] text-left transition-colors">
                    {m.avatar_url ? <img src={m.avatar_url} className="w-6 h-6 rounded-full" alt="" /> : <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-[9px] font-bold">{m.name?.charAt(0)}</div>}
                    <div><p className="text-xs font-medium">{m.name}</p>{m.department && <p className="text-[10px] text-[hsl(var(--text-muted))]">{m.department}</p>}</div>
                  </button>
                ))}
              </div>
            )}
            {conversations.length === 0 && !showNewChat ? (
              <div className="p-8 text-center text-sm text-[hsl(var(--text-muted))]"><Inbox size={24} className="mx-auto mb-2 opacity-30" />No conversations yet</div>
            ) : conversations.map(c => (
              <button key={c.partner_id} onClick={() => openChat(c.partner_id, c.partner_name, c.partner_avatar)}
                className={`w-full flex items-center gap-3 p-3 hover:bg-[hsl(var(--surface))] border-b border-[hsl(var(--border))] text-left transition-colors ${c.unread > 0 ? 'bg-blue-500/5' : ''}`}>
                {c.partner_avatar ? <img src={c.partner_avatar} className="w-8 h-8 rounded-full" alt="" /> : <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold">{c.partner_name?.charAt(0)}</div>}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between"><span className={`text-xs font-medium ${c.unread > 0 ? '' : 'text-[hsl(var(--text-secondary))]'}`}>{c.partner_name}</span><span className="text-[10px] text-[hsl(var(--text-muted))]">{formatTime(c.last_at)}</span></div>
                  <div className="flex items-center justify-between"><p className="text-[10px] text-[hsl(var(--text-muted))] truncate">{c.last_from === currentUser.id ? 'You: ' : ''}{c.last_message}</p>{c.unread > 0 && <span className="w-4 h-4 bg-blue-500 text-white text-[9px] rounded-full flex items-center justify-center flex-shrink-0">{c.unread}</span>}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// User Profile Popup Component
function UserProfilePopup({ user, onClose, onSendMessage }) {
  if (!user) return null;
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-2xl shadow-2xl w-72 overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="h-20 bg-gradient-to-r from-blue-500 to-indigo-600 relative">
          <button onClick={onClose} className="absolute top-2 right-2 w-6 h-6 bg-black/30 rounded-full flex items-center justify-center text-white hover:bg-black/50"><X size={12} /></button>
        </div>
        <div className="px-5 pb-5 -mt-10 relative">
          <div className="flex flex-col items-center">
            {user.avatar_url ? (
              <img src={user.avatar_url} alt="" className="w-20 h-20 rounded-full border-4 border-[hsl(var(--card))] object-cover shadow-lg" />
            ) : (
              <div className="w-20 h-20 rounded-full border-4 border-[hsl(var(--card))] bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-2xl font-bold shadow-lg">{user.name?.charAt(0)}</div>
            )}
            <h3 className="font-semibold text-base mt-3">{user.name}</h3>
            {(user.department || user.position) && <p className="text-xs text-[hsl(var(--text-muted))] mt-0.5">{[user.department, user.position].filter(Boolean).join(' - ')}</p>}
            <div className="flex items-center gap-1.5 mt-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-[10px] text-emerald-400 font-medium">Online</span>
            </div>
          </div>
          <div className="mt-4">
            <button onClick={() => { onSendMessage(user); onClose(); }} className="w-full flex items-center justify-center gap-2 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-xl text-sm font-medium transition-colors">
              <MessageCircle size={14} /> Send Message
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ResponsiveLayout({ children }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, toggleTheme } = useTheme();
  const { t } = useLanguage();
  const { onlineUsers } = useWebSocket() || {};
  
  // Online users for sidebar
  const currentUserId = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}').id; } catch { return null; } })();
  const allOnline = onlineUsers || [];
  const selfIncluded = allOnline.some(u => u.user_id === currentUserId);
  const displayOnline = selfIncluded ? allOnline : [
    ...(() => { try { const cu = JSON.parse(localStorage.getItem('user') || '{}'); return cu.id ? [{ user_id: cu.id, name: cu.name || 'You', avatar_url: cu.avatar_url }] : []; } catch { return []; } })(),
    ...allOnline
  ];
  
  // Admin access
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [tapCount, setTapCount] = useState(0);
  
  const DEFAULT_PIN = 'underads26';
  
  // Notifications & Search & Inbox
  const [notifications, setNotifications] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const [showInbox, setShowInbox] = useState(false);
  const [inboxUnread, setInboxUnread] = useState(0);
  const [inboxDirectUser, setInboxDirectUser] = useState(null);
  const [profilePopupUser, setProfilePopupUser] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showFavDropdown, setShowFavDropdown] = useState(false);
  const [favEditing, setFavEditing] = useState(false);
  const [favIbList, setFavIbList] = useState([]);
  const [favouritePages, setFavouritePages] = useState(() => {
    // Optimistic boot from localStorage cache, but DB is source of truth (fetched in useEffect below)
    try { return JSON.parse(localStorage.getItem('favourite_pages') || '[]'); } catch { return []; }
  });

  // Persist favourite pages to backend (per-user) + keep localStorage as cache for fast paint
  const saveFavourites = React.useCallback(async (next) => {
    setFavouritePages(next);
    try { localStorage.setItem('favourite_pages', JSON.stringify(next)); } catch {}
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/favourite-pages`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ favourite_pages: next })
      });
    } catch (e) { /* silent — localStorage still holds the cache */ }
  }, []);

  // Load favourites from backend on mount (overrides localStorage if user logged in)
  useEffect(() => {
    const loadFavs = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/favourite-pages`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          const remote = Array.isArray(data.favourite_pages) ? data.favourite_pages : [];
          setFavouritePages(remote);
          try { localStorage.setItem('favourite_pages', JSON.stringify(remote)); } catch {}
        }
      } catch {}
    };
    loadFavs();
  }, []);
  
  useEffect(() => {
    const fetchNotifs = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const [nRes, iRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/inbox/unread-count`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null)
        ]);
        if (nRes.ok) { const data = await nRes.json(); setNotifications(data.notifications || []); }
        if (iRes?.ok) { const data = await iRes.json(); setInboxUnread(data.unread || 0); }
      } catch {}
    };
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 30000);
    return () => clearInterval(interval);
  }, []);

  // Global Search Handler
  const handleGlobalSearch = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/global-search?q=${encodeURIComponent(query)}`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data.results || []);
      }
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setSearching(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) handleGlobalSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSearchResultClick = (result) => {
    setShowSearch(false);
    setSearchQuery('');
    setSearchResults([]);
    navigate(result.link);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('selectedIB');
    clearPersistedViewState();
    // Hard redirect so no lingering effect can re-persist view state
    window.location.assign('/login');
  };

  const handleNavigate = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const handleLogoTap = () => {
    setTapCount(prev => {
      const newCount = prev + 1;
      if (newCount >= 6) {
        setShowPinModal(true);
        return 0;
      }
      setTimeout(() => setTapCount(0), 3000);
      return newCount;
    });
  };

  const handlePinSubmit = () => {
    const storedPin = localStorage.getItem('adminPin') || DEFAULT_PIN;
    if (pinInput === storedPin) {
      setShowPinModal(false);
      setPinInput('');
      toast.success('Admin access granted!');
      navigate('/admin-panel');
    } else {
      toast.error('Wrong PIN');
      setPinInput('');
    }
  };

  const isActive = (path) => location.pathname === path;
  const isIBActive = location.pathname.includes('/ib-management') || location.pathname.includes('/ib/');
  const isTeamActive = location.pathname.includes('/team');
  const isMySpaceActive = location.pathname === '/my-tasks' || location.pathname === '/idea-parking' || location.pathname === '/my-scorecard' || location.pathname === '/projects' || location.pathname === '/calendar';
  const isFinanceActive = location.pathname.includes('/finance');
  const [ibExpanded, setIbExpanded] = useState(false);
  const [teamExpanded, setTeamExpanded] = useState(false);
  const [financeExpanded, setFinanceExpanded] = useState(false);
  const [mySpaceExpanded, setMySpaceExpanded] = useState(false);

  // Auto-expand when a child route is active
  useEffect(() => {
    if (isIBActive) setIbExpanded(true);
    if (isTeamActive) setTeamExpanded(true);
    if (isFinanceActive) setFinanceExpanded(true);
    if (isMySpaceActive) setMySpaceExpanded(true);
  }, [location.pathname]);

  const NavItem = ({ icon: Icon, label, path, onClick, isSubmenu = false }) => {
    const active = isActive(path);
    return (
      <Button
        variant="ghost"
        size={isSubmenu ? "sm" : "default"}
        className={`w-full justify-start ${isSubmenu ? 'text-xs' : ''} ${
          active
            ? 'bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/20' 
            : 'text-[hsl(var(--text-secondary))] hover:bg-[hsl(var(--surface-elevated))] hover:text-[hsl(var(--text-primary))]'
        }`}
        onClick={onClick || (() => handleNavigate(path))}
      >
        <Icon size={isSubmenu ? 14 : 18} className={sidebarCollapsed && !isSubmenu ? '' : 'mr-2'} />
        {(!sidebarCollapsed || isSubmenu) && <span>{label}</span>}
      </Button>
    );
  };

  // Get user permissions from localStorage
  const getUserPerms = () => {
    try { return JSON.parse(localStorage.getItem('user') || '{}').permissions || {}; } catch { return {}; }
  };
  const perms = getUserPerms();
  
  // Check if team space sub-page permissions are explicitly set
  const tsSubKeys = ['ts_announcements', 'ts_mood', 'ts_ideas', 'ts_creative', 'ts_voting', 'ts_standup', 'ts_leaderboard'];
  const hasExplicitTeamPerms = tsSubKeys.some(k => perms[k] !== undefined);
  
  const hasPerm = (key) => {
    // For team space sub-pages: if ANY sub-page is explicitly set, require explicit true
    if (key.startsWith('ts_') && hasExplicitTeamPerms) {
      return perms[key] === true;
    }
    // For everything else: undefined = allowed (deny-list approach)
    return perms[key] !== false;
  };
  const isChannelRestricted = perms.channel_access === 'specific';
  const allowedChannels = perms.allowed_channels || [];
  
  // Check if a module is EXPLICITLY enabled (admin override) — should show even if channel-restricted
  const isExplicitlyEnabled = (key) => perms[key] === true;

  const SidebarContent = ({ isMobile = false }) => (
    <>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {hasPerm('dashboard') && (!isChannelRestricted || isExplicitlyEnabled('dashboard')) && <NavItem icon={LayoutDashboard} label={t('dashboard')} path="/dashboard" />}
        
        {/* My Space Group */}
        <div>
          <Button
            variant="ghost"
            className={`w-full justify-start ${isMySpaceActive ? 'bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]' : 'text-[hsl(var(--text-secondary))] hover:bg-[hsl(var(--surface-elevated))]'}`}
            onClick={() => setMySpaceExpanded(!mySpaceExpanded)}
          >
            <CheckSquare size={18} className={sidebarCollapsed ? '' : 'mr-2'} />
            {!sidebarCollapsed && (
              <>
                <span className="flex-1 text-left">My Space</span>
                {mySpaceExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </>
            )}
          </Button>
          {mySpaceExpanded && !sidebarCollapsed && (
            <div className="ml-3 mt-1 space-y-0.5 border-l border-[hsl(var(--border))] pl-2">
              <NavItem icon={CheckSquare} label="My Tasks" path="/my-tasks" isSubmenu />
              <NavItem icon={FolderKanban} label="Project Management" path="/projects" isSubmenu />
              <NavItem icon={Calendar} label="Team Calendar" path="/calendar" isSubmenu />
              <NavItem icon={Lightbulb} label="Idea Parking" path="/idea-parking" isSubmenu />
              <NavItem icon={Target} label="My Scorecard" path="/my-scorecard" isSubmenu />
            </div>
          )}
        </div>
        
        {/* IB Management Group — if channel restricted, show direct link to allowed channel */}
        {hasPerm('ib_management') && (!isChannelRestricted || isExplicitlyEnabled('ib_management')) && (
        <div>
          <Button
            variant="ghost"
            className={`w-full justify-start ${
              isIBActive
                ? 'bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]' 
                : 'text-[hsl(var(--text-secondary))] hover:bg-[hsl(var(--surface-elevated))]'
            }`}
            onClick={() => setIbExpanded(!ibExpanded)}
          >
            <List size={18} className={sidebarCollapsed ? '' : 'mr-2'} />
            {!sidebarCollapsed && (
              <>
                <span className="flex-1 text-left">{t('ibManagement')}</span>
                {ibExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </>
            )}
          </Button>
          {ibExpanded && !sidebarCollapsed && (
            <div className="ml-3 mt-1 space-y-0.5 border-l border-[hsl(var(--border))] pl-2">
              <NavItem icon={Target} label="Dashboard" path="/ib-management/dashboard" isSubmenu />
              <NavItem icon={Calendar} label="Yearly Snapshot" path="/ib-management/yearly-snapshot" isSubmenu />
              <NavItem icon={BarChart3} label={t('overallPerformance')} path="/ib-management/overview" isSubmenu />
              <NavItem icon={List} label={t('ibList')} path="/ib-management/list" isSubmenu />
              <NavItem icon={Crosshair} label="War Room" path="/ib-management/war-room" isSubmenu />
              <NavItem icon={Stethoscope} label="Diagnosis Center" path="/ib-management/diagnosis" isSubmenu />
              <NavItem icon={FileText} label={t('campaigns')} path="/ib-management/campaigns" isSubmenu />
            </div>
          )}
        </div>
        )}

        {/* Channel-specific access — show only assigned channels */}
        {isChannelRestricted && allowedChannels.length > 0 && (
          <div className="space-y-0.5">
            {allowedChannels.map(chId => (
              <NavItem key={chId} icon={List} label={chId.replace('ib-', '').replace(/-/g, ' ').toUpperCase()} path={`/ib/${chId}/details`} />
            ))}
          </div>
        )}

        {/* Team Space Group */}
        {hasPerm('team_space') && (!isChannelRestricted || isExplicitlyEnabled('team_space')) && (
        <div>
          <Button
            variant="ghost"
            className={`w-full justify-start ${
              isTeamActive
                ? 'bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]' 
                : 'text-[hsl(var(--text-secondary))] hover:bg-[hsl(var(--surface-elevated))]'
            }`}
            onClick={() => setTeamExpanded(!teamExpanded)}
          >
            <Users size={18} className={sidebarCollapsed ? '' : 'mr-2'} />
            {!sidebarCollapsed && (
              <>
                <span className="flex-1 text-left">{t('teamSpace')}</span>
                {teamExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </>
            )}
          </Button>
          {teamExpanded && !sidebarCollapsed && (
            <div className="ml-3 mt-1 space-y-0.5 border-l border-[hsl(var(--border))] pl-2">
              {hasPerm('ts_announcements') && <NavItem icon={Megaphone} label={t('announcements')} path="/team/announcements" isSubmenu />}
              {hasPerm('ts_mood') && <NavItem icon={Smile} label={t('dailyEcstasy')} path="/team/mood" isSubmenu />}
              {hasPerm('ts_ideas') && <NavItem icon={Lightbulb} label={t('ideasFeedback')} path="/team/ideas-feedback" isSubmenu />}
              {hasPerm('ts_creative') && <NavItem icon={Palette} label={t('creativeRequest')} path="/team/creative-request" isSubmenu />}
              {hasPerm('ts_creative') && <NavItem icon={Trophy} label="Creative Performance" path="/team/creative-performance" isSubmenu />}
              {hasPerm('ts_voting') && <NavItem icon={Vote} label={t('votingPolls')} path="/team/voting" isSubmenu />}
              {hasPerm('ts_standup') && <NavItem icon={ClipboardList} label={t('weeklyStandup')} path="/team/standup" isSubmenu />}
              {hasPerm('ts_leaderboard') && <NavItem icon={Trophy} label={t('leaderboard')} path="/team/leaderboard" isSubmenu />}
            </div>
          )}
        </div>
        )}
        
        {hasPerm('resource_library') && (!isChannelRestricted || isExplicitlyEnabled('resource_library')) && <NavItem icon={BookOpen} label={t('uaResourceLibrary')} path="/resources" />}
        
        {/* Finance Group - Permission-based access */}
        {hasPerm('finance_access') && (!isChannelRestricted || isExplicitlyEnabled('finance_access')) && (
        <div>
          <button
            onClick={() => setFinanceExpanded(!financeExpanded)}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
              isFinanceActive ? 'bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]' : 'text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--text-primary))] hover:bg-[hsl(var(--surface-elevated))]'
            }`}
          >
            <Wallet size={18} />
            {!sidebarCollapsed && <><span className="flex-1 text-left">Finance</span><ChevronDown size={14} className={`transition-transform ${financeExpanded ? 'rotate-180' : ''}`} /></>}
          </button>
          {financeExpanded && !sidebarCollapsed && (
            <div className="ml-3 mt-1 space-y-0.5 border-l-2 border-[hsl(var(--border))] pl-3">
              <NavItem icon={Send} label="Fund Request" path="/finance/fund-request" isSubmenu />
              <NavItem icon={Wallet} label="Marketing Fund" path="/finance/marketing-fund" isSubmenu />
              <NavItem icon={PieChart} label="Channel Budget" path="/finance/channel-budget" isSubmenu />
            </div>
          )}
        </div>
        )}
        
        {/* AI Agent */}
        <NavItem icon={Bot} label="AI Agent" path="/ai-agent" />
        
        <NavItem icon={Settings} label={t('settings')} path="/settings" />
      </nav>
      
      {/* Online Users */}
      {!sidebarCollapsed && (
        <div className="px-3 py-2 border-t border-[hsl(var(--border))]">
          <p className="text-[10px] font-semibold text-[hsl(var(--text-muted))] uppercase tracking-wider mb-2 px-1 flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            Online ({displayOnline.length})
          </p>
          <div className="space-y-0.5 max-h-28 overflow-y-auto" data-testid="sidebar-online-users">
            {displayOnline.map((u) => (
              <button key={u.user_id} onClick={() => { if (u.user_id !== currentUserId) setProfilePopupUser(u); }} className={`w-full flex items-center gap-2 px-1 py-1 rounded-md text-left ${u.user_id !== currentUserId ? 'hover:bg-[hsl(var(--surface-elevated))] cursor-pointer' : ''} transition-colors`}>
                <div className="relative flex-shrink-0">
                  {u.avatar_url ? (
                    <img src={u.avatar_url} alt="" className="w-6 h-6 rounded-full object-cover" />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white text-[8px] font-bold">
                      {u.name?.charAt(0) || '?'}
                    </div>
                  )}
                  <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 bg-emerald-400 rounded-full border border-[hsl(var(--background))]" />
                </div>
                <span className="text-xs text-[hsl(var(--text-secondary))] truncate">
                  {u.name}{u.user_id === currentUserId ? ' (Anda)' : ''}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
      {sidebarCollapsed && (
        <div className="px-2 py-2 border-t border-[hsl(var(--border))] flex flex-col items-center gap-1">
          {displayOnline.slice(0, 3).map((u) => (
            <div key={u.user_id} className="relative" title={u.name}>
              {u.avatar_url ? (
                <img src={u.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover" />
              ) : (
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white text-[9px] font-bold">
                  {u.name?.charAt(0) || '?'}
                </div>
              )}
              <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-400 rounded-full border-[1.5px] border-[hsl(var(--background))]" />
            </div>
          ))}
          {displayOnline.length > 3 && <span className="text-[9px] text-emerald-400">+{displayOnline.length - 3}</span>}
        </div>
      )}
      
      <div className="p-3 border-t border-[hsl(var(--border))]">
        <Button
          variant="ghost"
          className="w-full justify-start text-red-500 hover:bg-red-500/10"
          onClick={handleLogout}
        >
          <LogOut size={18} className={sidebarCollapsed ? '' : 'mr-2'} />
          {!sidebarCollapsed && <span>{t('logout')}</span>}
        </Button>
      </div>
    </>
  );

  return (
    <SidebarContext.Provider value={{ isCollapsed: sidebarCollapsed, setIsCollapsed: setSidebarCollapsed }}>
      <div className="min-h-screen bg-[hsl(var(--background))]">
        
        {/* ===== MOBILE HEADER ===== */}
        <header className="lg:hidden fixed top-0 left-0 right-0 z-50 h-14 bg-[hsl(var(--surface))] border-b border-[hsl(var(--border))] flex items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="w-9 h-9 rounded-lg bg-[hsl(var(--surface-elevated))] flex items-center justify-center text-[hsl(var(--text-secondary))]"
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            <button
              onClick={() => setShowNotifs(!showNotifs)}
              className="w-9 h-9 rounded-lg bg-[hsl(var(--surface-elevated))] flex items-center justify-center text-[hsl(var(--text-secondary))] relative"
              data-testid="notif-bell-mobile"
            >
              <Bell size={18} />
              {notifications.filter(n => !n.read).length > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">{notifications.filter(n => !n.read).length}</span>
              )}
            </button>
            <button
              onClick={toggleTheme}
              className="w-9 h-9 rounded-lg bg-[hsl(var(--surface-elevated))] flex items-center justify-center text-[hsl(var(--text-secondary))]"
            >
              {isDark ? <Sun size={18} /> : <Moon size={18} />}
            </button>
          </div>
          <img 
            src="https://customer-assets.emergentagent.com/job_channel-crm-system/artifacts/4egfgzp6_ua%20logo.png" 
            alt="Underads" 
            className="h-6"
          />
        </header>

        {/* ===== MOBILE MENU OVERLAY ===== */}
        {mobileMenuOpen && (
          <div 
            className="lg:hidden fixed inset-0 z-40 bg-black/50"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}

        {/* ===== MOBILE SLIDE MENU ===== */}
        <aside className={`
          lg:hidden fixed top-14 left-0 bottom-0 z-50 w-64
          bg-[hsl(var(--surface))] border-r border-[hsl(var(--border))]
          transform transition-transform duration-300 ease-in-out
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          flex flex-col
        `}>
          <SidebarContent isMobile />
        </aside>

        {/* ===== DESKTOP SIDEBAR ===== */}
        <aside className={`
          hidden lg:flex fixed top-0 left-0 bottom-0 z-40
          bg-[hsl(var(--surface))] border-r border-[hsl(var(--border))]
          flex-col transition-all duration-300
          ${sidebarCollapsed ? 'w-16' : 'w-64'}
        `}>
          {/* Sidebar Header */}
          <div className="h-14 px-4 border-b border-[hsl(var(--border))] flex items-center justify-between">
            {!sidebarCollapsed && (
              <div className="flex-1 flex items-center justify-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_channel-crm-system/artifacts/4egfgzp6_ua%20logo.png" 
                  alt="Underads" 
                  className="h-8 cursor-default select-none"
                  onClick={handleLogoTap}
                  draggable="false"
                />
              </div>
            )}
            <div className="flex items-center gap-1">
              <button
                onClick={toggleTheme}
                className="w-8 h-8 rounded-lg bg-[hsl(var(--surface-elevated))] flex items-center justify-center text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--text-primary))]"
              >
                {isDark ? <Sun size={14} /> : <Moon size={14} />}
              </button>
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="w-8 h-8 rounded-lg bg-[hsl(var(--surface-elevated))] flex items-center justify-center text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--text-primary))]"
              >
                {sidebarCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
              </button>
            </div>
          </div>
          
          <SidebarContent />
        </aside>

        {/* ===== MAIN CONTENT ===== */}
        <main className={`
          h-screen overflow-y-auto transition-all duration-300
          pt-14 lg:pt-0
          ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}
        `}>
          {/* Desktop Top Bar */}
          <div className="hidden lg:flex items-center justify-end gap-3 px-6 py-3 border-b border-[hsl(var(--border))] bg-[hsl(var(--background))]/80 backdrop-blur-sm sticky top-0 z-50">
            <div className="flex items-center gap-3">
              {/* Global Search Icon */}
              <div className="relative">
                <button
                  onClick={() => setShowSearch(!showSearch)}
                  className="w-9 h-9 rounded-lg bg-[hsl(var(--surface))] border border-[hsl(var(--border))] flex items-center justify-center text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--text-primary))] hover:border-[hsl(var(--primary))]/30 transition-all"
                  data-testid="search-icon"
                >
                  <Search size={16} />
                </button>
                {showSearch && (
                  <div className="absolute top-0 right-12 w-96 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl shadow-2xl overflow-hidden z-[9999]" data-testid="search-panel">
                    <div className="p-3 border-b border-[hsl(var(--border))]">
                      <div className="relative">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--text-muted))]" />
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Search IB, tasks, announcements, requests..."
                          autoFocus
                          className="w-full h-10 pl-10 pr-4 rounded-lg bg-[hsl(var(--surface))] border border-[hsl(var(--border))] text-sm text-[hsl(var(--text-primary))] placeholder:text-[hsl(var(--text-muted))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/50"
                          data-testid="search-input"
                        />
                      </div>
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {searching ? (
                        <div className="p-6 text-center">
                          <div className="w-5 h-5 border-2 border-[hsl(var(--primary))] border-t-transparent rounded-full animate-spin mx-auto" />
                          <p className="text-xs text-[hsl(var(--text-muted))] mt-2">Searching...</p>
                        </div>
                      ) : searchResults.length > 0 ? (
                        <div className="divide-y divide-[hsl(var(--border))]">
                          {searchResults.map((result, idx) => (
                            <button
                              key={idx}
                              onClick={() => handleSearchResultClick(result)}
                              className="w-full p-3 text-left hover:bg-[hsl(var(--surface))] transition-colors flex items-start gap-3"
                            >
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs flex-shrink-0 ${
                                result.type === 'ib' ? 'bg-blue-500' :
                                result.type === 'announcement' ? 'bg-amber-500' :
                                result.type === 'creative_request' ? 'bg-pink-500' :
                                result.type === 'task' ? 'bg-emerald-500' :
                                result.type === 'standup' ? 'bg-indigo-500' :
                                'bg-gray-500'
                              }`}>
                                {result.type === 'ib' ? 'IB' : 
                                 result.type === 'announcement' ? 'AN' :
                                 result.type === 'creative_request' ? 'CR' :
                                 result.type === 'task' ? 'TK' :
                                 result.type === 'standup' ? 'SU' : '??'}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-[hsl(var(--text-primary))] truncate">{result.title}</p>
                                <p className="text-xs text-[hsl(var(--text-muted))] truncate">{result.subtitle}</p>
                              </div>
                            </button>
                          ))}
                        </div>
                      ) : searchQuery ? (
                        <div className="p-6 text-center">
                          <Search size={24} className="mx-auto text-[hsl(var(--text-muted))] opacity-30 mb-2" />
                          <p className="text-sm text-[hsl(var(--text-muted))]">No results found</p>
                        </div>
                      ) : (
                        <div className="p-4 text-center text-xs text-[hsl(var(--text-muted))]">
                          Type to search across IBs, tasks, announcements, and more...
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Inbox Icon */}
              <div className="relative">
                <button
                  onClick={() => { setShowInbox(!showInbox); setShowNotifs(false); }}
                  className="w-9 h-9 rounded-lg bg-[hsl(var(--surface))] border border-[hsl(var(--border))] flex items-center justify-center text-[hsl(var(--text-secondary))] hover:text-blue-400 hover:border-blue-500/30 transition-all"
                  data-testid="inbox-icon-desktop"
                >
                  <MessageCircle size={16} />
                  {inboxUnread > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-blue-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">{inboxUnread}</span>
                  )}
                </button>
                {showInbox && <>
                  <div className="fixed inset-0 z-[9998]" onClick={() => { setShowInbox(false); setInboxDirectUser(null); }} />
                  <InboxPanel onClose={() => { setShowInbox(false); setInboxDirectUser(null); }} directUser={inboxDirectUser} />
                </>}
              </div>

              {/* Favourite Pages Star */}
              <div className="relative">
                <button
                  onClick={() => { setShowFavDropdown(!showFavDropdown); setShowNotifs(false); setShowInbox(false); }}
                  className={`w-9 h-9 rounded-lg border flex items-center justify-center transition-all ${showFavDropdown || favouritePages.length > 0 ? 'bg-[#8b5cf6]/10 border-[#8b5cf6]/30 text-[#a78bfa]' : 'bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--text-primary))] hover:border-[#8b5cf6]/30'}`}
                  data-testid="fav-star-btn"
                >
                  <Star size={15} className={favouritePages.length > 0 ? 'fill-[#8b5cf6]/40' : ''} />
                </button>
                {showFavDropdown && <>
                  <div className="fixed inset-0 z-[9998]" onClick={() => { setShowFavDropdown(false); setFavEditing(false); }} />
                  <div className="absolute right-0 top-12 z-[9999] w-72 bg-[#0a0a0f] border border-[hsl(var(--border))] rounded-xl shadow-2xl shadow-black/50 overflow-hidden" data-testid="fav-dropdown">
                    <div className="p-3 border-b border-[hsl(var(--border))] flex items-center justify-between">
                      <span className="text-xs font-semibold text-white flex items-center gap-1.5"><Star size={12} className="text-[#8b5cf6]" /> Quick Pages</span>
                      <button onClick={() => {
                        if (!favEditing && favIbList.length === 0) {
                          const token = localStorage.getItem('token');
                          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ib/list`, { headers: { Authorization: `Bearer ${token}` } })
                            .then(r => r.json()).then(d => setFavIbList((d || []).filter(ib => (ib.status || 'active') === 'active').map(ib => ({ id: ib.id, name: ib.name })))).catch(() => {});
                        }
                        setFavEditing(!favEditing);
                      }} className={`text-[9px] px-2 py-0.5 rounded ${favEditing ? 'bg-[#8b5cf6] text-white' : 'text-[#8b5cf6] hover:text-white'}`}>
                        {favEditing ? 'Done' : 'Edit'}
                      </button>
                    </div>
                    <div className="max-h-[350px] overflow-y-auto">
                      {!favEditing ? (
                        <div className="p-2">
                          {favouritePages.length === 0 && (
                            <p className="text-[11px] text-[hsl(var(--text-muted))] text-center py-4">No favourites yet. Click Edit to add.</p>
                          )}
                          {favouritePages.map(id => {
                            const GENERAL = { 'dashboard': { l: 'Dashboard', p: '/' }, 'war-room': { l: 'War Room HQ', p: '/ib-management/war-room' }, 'overall': { l: 'Overall Performance', p: '/ib-management/overall-performance' }, 'list-ib': { l: 'List of IB', p: '/ib-management/list' }, 'reports': { l: 'Reports', p: '/ib-management/reports' }, 'creative': { l: 'Creative Requests', p: '/team-space/creative-requests' }, 'fund': { l: 'Fund Requests', p: '/finance/fund-requests' }, 'ai-agent': { l: 'AI Agent', p: '/ai-agent' }, 'announcements': { l: 'Announcements', p: '/team-space/announcements' }, 'ideas': { l: 'Ideas Board', p: '/team-space/ideas' }, 'leaderboard': { l: 'Leaderboard', p: '/ib-management/leaderboard' }, 'idea-parking': { l: 'Idea Parking', p: '/idea-parking' } };
                            const SUB_MAP = { 'overview': 'Overview', 'performance': 'Performance', 'war-room': 'War Room', 'analytic': 'Analytic', 'daily': 'Daily Data', 'database': 'Database', 'product-install': 'Product Installation' };
                            if (GENERAL[id]) {
                              return (
                                <a key={id} href={GENERAL[id].p} onClick={() => setShowFavDropdown(false)} className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[hsl(var(--surface-elevated))] transition-all text-xs text-white" data-testid={`favd-${id}`}>
                                  <Star size={10} className="text-[#8b5cf6] shrink-0" /> {GENERAL[id].l}
                                </a>
                              );
                            }
                            if (id.includes('__')) {
                              const [ibId, suffix] = id.split('__');
                              const subLabel = SUB_MAP[suffix] || suffix;
                              const ibName = favIbList.find(ib => ib.id === ibId)?.name || ibId.replace('ib-', '').replace(/-\d+/g, '').toUpperCase();
                              return (
                                <a key={id} href={`/ib/${ibId}/details?tab=${suffix}`} onClick={() => setShowFavDropdown(false)} className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[hsl(var(--surface-elevated))] transition-all text-xs" data-testid={`favd-${id}`}>
                                  <Star size={10} className="text-[#a78bfa] shrink-0" />
                                  <span className="text-[#a78bfa]">{ibName}</span>
                                  <span className="text-[hsl(var(--text-muted))]">/</span>
                                  <span className="text-white">{subLabel}</span>
                                </a>
                              );
                            }
                            return null;
                          })}
                        </div>
                      ) : (
                        <div className="p-3 space-y-3">
                          <div>
                            <p className="text-[9px] uppercase tracking-wider text-[hsl(var(--text-muted))] font-bold mb-1.5">General</p>
                            <div className="flex flex-wrap gap-1">
                              {[['dashboard','Dashboard'],['war-room','War Room HQ'],['overall','Overall Performance'],['list-ib','List of IB'],['reports','Reports'],['creative','Creative Requests'],['fund','Fund Requests'],['ai-agent','AI Agent'],['announcements','Announcements'],['ideas','Ideas Board'],['leaderboard','Leaderboard'],['idea-parking','Idea Parking']].map(([pid, label]) => (
                                <button key={pid} onClick={() => { const u = favouritePages.includes(pid) ? favouritePages.filter(f => f !== pid) : [...favouritePages, pid]; saveFavourites(u); }}
                                  className={`px-2 py-1 rounded text-[10px] border transition-all ${favouritePages.includes(pid) ? 'bg-[#8b5cf6]/20 border-[#8b5cf6]/50 text-white' : 'bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-[hsl(var(--text-muted))] hover:text-white'}`}>
                                  {favouritePages.includes(pid) && <CheckCircle2 size={8} className="inline mr-0.5 text-[#8b5cf6]" />}{label}
                                </button>
                              ))}
                            </div>
                          </div>
                          {favIbList.length > 0 && (
                            <div>
                              <p className="text-[9px] uppercase tracking-wider text-[hsl(var(--text-muted))] font-bold mb-1.5">IB Deep Links</p>
                              <div className="space-y-1.5 max-h-[150px] overflow-y-auto">
                                {favIbList.map(ib => (
                                  <div key={ib.id}>
                                    <p className="text-[9px] font-semibold text-[#a78bfa] mb-0.5">{ib.name}</p>
                                    <div className="flex flex-wrap gap-1 ml-1">
                                      {[['overview','Overview'],['performance','Perf'],['war-room','War Room'],['analytic','Analytic'],['daily','Daily'],['database','DB'],['product-install','Product']].map(([suffix, label]) => {
                                        const fid = `${ib.id}__${suffix}`;
                                        return (
                                          <button key={fid} onClick={() => { const u = favouritePages.includes(fid) ? favouritePages.filter(f => f !== fid) : [...favouritePages, fid]; saveFavourites(u); }}
                                            className={`px-1.5 py-0.5 rounded text-[9px] border transition-all ${favouritePages.includes(fid) ? 'bg-[#8b5cf6]/20 border-[#8b5cf6]/50 text-white' : 'bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-[hsl(var(--text-muted))] hover:text-white'}`}>
                                            {label}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </>}
              </div>

              {/* Notification Bell */}
              <div className="relative">
                <button
                  onClick={() => { setShowNotifs(!showNotifs); setShowInbox(false); }}
                  className="w-9 h-9 rounded-lg bg-[hsl(var(--surface))] border border-[hsl(var(--border))] flex items-center justify-center text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--text-primary))] hover:border-[hsl(var(--primary))]/30 transition-all"
                  data-testid="notif-bell-desktop"
                >
                  <Bell size={16} />
                  {notifications.filter(n => !n.read).length > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">{notifications.filter(n => !n.read).length}</span>
                  )}
                </button>
                {showNotifs && <>
                  <div className="fixed inset-0 z-[9998]" onClick={() => setShowNotifs(false)} />
                  <NotificationPanel notifications={notifications} setNotifications={setNotifications} onClose={() => setShowNotifs(false)} onNavigate={(path) => { navigate(path); setShowNotifs(false); }} />
                </>}
              </div>

              {/* User info */}
              <div className="flex items-center gap-3 pl-2 border-l border-[hsl(var(--border))]">
                {/* Online Users Indicator */}
                <OnlineUsersIndicator onUserClick={(u) => setProfilePopupUser(u)} />
                
                {(() => {
                  try {
                    const u = JSON.parse(localStorage.getItem('user') || '{}');
                    const avatarUrl = u.avatar_url;
                    const initial = u.name?.charAt(0) || '?';
                    return avatarUrl ? (
                      <img src={avatarUrl} alt="" className="w-8 h-8 rounded-lg object-cover" />
                    ) : (
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--brand-orange))] flex items-center justify-center text-white text-xs font-bold">{initial}</div>
                    );
                  } catch { return <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--brand-orange))] flex items-center justify-center text-white text-xs font-bold">?</div>; }
                })()}
                <div className="text-right">
                  <p className="text-sm font-medium text-[hsl(var(--text-primary))] leading-tight">{(() => { try { return JSON.parse(localStorage.getItem('user') || '{}').name || 'User'; } catch { return 'User'; } })()}</p>
                  <p className="text-[10px] text-[hsl(var(--text-muted))] leading-tight">{(() => { try { const u = JSON.parse(localStorage.getItem('user') || '{}'); const dept = u.department || ''; const pos = u.position || ''; return dept && pos ? `${dept} - ${pos}` : dept || pos || u.role_id || ''; } catch { return ''; } })()}</p>
                </div>
              </div>
            </div>
          </div>
          {children}
        </main>

        {/* Admin PIN Modal */}
        <Dialog open={showPinModal} onOpenChange={setShowPinModal}>
          <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-xs">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 justify-center text-[hsl(var(--text-primary))]">
                <Shield size={20} className="text-[hsl(var(--primary))]" />
                Admin Access
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <p className="text-xs text-[hsl(var(--text-muted))] text-center">Enter PIN to access Admin Panel</p>
              <Input 
                type="password"
                value={pinInput}
                onChange={(e) => setPinInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handlePinSubmit()}
                placeholder="••••••"
                className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-[hsl(var(--text-primary))] text-center text-lg tracking-widest"
                maxLength={10}
                autoFocus
              />
              <Button 
                onClick={handlePinSubmit}
                className="w-full bg-[hsl(var(--primary))] text-white"
              >
                <Lock size={14} className="mr-2" />
                Enter
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Daily Mood & Quote Popups */}
        <DailyPopups />

        {/* User Profile Popup */}
        {profilePopupUser && (
          <UserProfilePopup 
            user={profilePopupUser} 
            onClose={() => setProfilePopupUser(null)}
            onSendMessage={(u) => { setInboxDirectUser(u); setShowInbox(true); setShowNotifs(false); }}
          />
        )}
      </div>
    </SidebarContext.Provider>
  );
}
